import {syncService} from "@/utilities/sync/SyncService.ts";
import {APP_CONSTANTS} from "@/utilities/constants/AppConstants.ts";

interface User {
  id: string;
  email: string;
  name?: string;
  createdAt: string;
}

interface AuthResponse {
  user: User;
  access_token: string;
  refreshToken?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
  name?: string;
}

class AuthService {
  private readonly API_BASE_URL = APP_CONSTANTS.API_BASE_URL;
  private readonly TOKEN_KEY = 'bookmarker_auth_token';
  private readonly REFRESH_TOKEN_KEY = 'bookmarker_refresh_token';
  private readonly USER_KEY = 'bookmarker_user';

  // Get stored token
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // Get stored refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  // Get stored user
  getUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  // Store authentication data
  private storeAuthData(data: AuthResponse): void {
    localStorage.setItem(this.TOKEN_KEY, data.access_token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(data.user));

    // Also sync to extension storage if available
    this.syncToExtension(data);
  }

  // Sync auth data to extension storage
  private syncToExtension(data: AuthResponse): void {
    try {
      // Check if we're in a Chrome extension environment
      if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
        chrome.storage.local.set({
          bookmarker_auth_token: data.access_token,
          bookmarker_user: JSON.stringify(data.user)
        });
      }
    } catch (error) {
      console.error('Failed to sync auth to extension:', error);
    }
  }

  // Clear authentication data
  clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);

    // Also clear from extension storage
    this.clearFromExtension();
  }

  // Clear auth data from extension storage
  private clearFromExtension(): void {
    try {
      if (typeof chrome !== 'undefined' && chrome.storage) {
        chrome.storage.local.remove([
          'bookmarker_auth_token',
          'bookmarker_user',
          'bookmarker_refresh_token'
        ], () => {
          console.log('Auth tokens cleared from extension storage');
        });
      }
    } catch (error) {
      console.error('Failed to clear extension auth:', error);
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      const data: AuthResponse = await response.json();
      this.storeAuthData(data);
      await syncService.fetchFromBackend();
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Register user
  async register(userData: RegisterData): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Registration failed');
      }

      const data: AuthResponse = await response.json();
      this.storeAuthData(data);
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        // Call logout endpoint to invalidate token
        await fetch(`${this.API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuthData();
    }
  }

  // Refresh token
  async refreshToken(): Promise<AuthResponse> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${this.API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data: AuthResponse = await response.json();
      this.storeAuthData(data);
      return data;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuthData();
      throw error;
    }
  }

  // Get authenticated headers for API requests
  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }
}

export const authService = new AuthService();
export type { User, AuthResponse, LoginCredentials, RegisterData };
